import * as yup from 'yup';
import type { AnyObject, AnyObjectSchema } from 'yup';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { ConfigModel } from '@ibe/services';
import { FormRow, ElementSchema, Validations } from '../types/types';
import { isValidations } from '../types/typeGuards';
import { DefaultValues, FieldValues } from 'react-hook-form';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

type IsOlderThanAgeOnDateContext = {
  isOlderThanAgeOnDateMinAge: number;
  isOlderThanAgeOnDateStartDate: string;
  isOlderThanAgeOnDateDayProp: string;
  isOlderThanAgeOnDateMonthProp: string;
};

type StartDateContext = {
  startDate: string;
};

type AgeOnDateContext = {
  adultMinAge: number;
  startDate: string;
};

type ExternalAgeContext = {
  adultMinAge: number;
  startDate: string;
  birthDate: string;
};

type ChildAgeContext = {
  childMinAge: number;
  startDate: string;
};

type AdultContext = {
  adultMinAge: number;
};

type AgeComparisonContext = {
  startDate: string;
  endDate: string;
  birthDate: string;
  age: number;
};

type ExactAgeContext = {
  selectedAge: number;
  selectedDate: string;
  birthDate: string;
};

type EmailContext = {
  emailProp: string;
};

type ValidYearContext = {
  minYearOfBirth: number;
};

type NotAfterDateContext = {
  date: string;
  birthDate: string;
};

yup.addMethod(
  yup.array,
  'arrayCustomProp',
  function arrayCustomProp(properties: string[], requiredProperties?: string[]) {
    return this.test({
      test: arr =>
        !!arr &&
        arr.length > 0 &&
        ((!requiredProperties && properties.some(property => arr[0][property] > 0)) ||
          (!!requiredProperties &&
            properties.some(property => arr[0][property] > 0) &&
            requiredProperties.every(property => arr[0][property] > 0)))
    });
  }
);

yup.addMethod(yup.string, 'zipCode', function zipCode(message?: string) {
  return this.matches(/^[0-9]{5}$/, message);
});

yup.addMethod(yup.string, 'phone', function phone(message?: string) {
  return this.matches(/^[+]*[\s/0-9]+$/, message);
});

yup.addMethod(yup.string, 'countryCode', function countryCode(message?: string) {
  return this.matches(/^\+?\d{1,5}$/, message);
});

yup.addMethod(yup.object, 'isOlderThanAgeOnDate', function isOlderThanAgeOnDate(message?: string) {
  return this.test({
    message,

    test(value, context) {
      const contextValues = context.options.context as IsOlderThanAgeOnDateContext | undefined;

      const dayProp = contextValues?.isOlderThanAgeOnDateDayProp;

      const monthProp = contextValues?.isOlderThanAgeOnDateMonthProp;

      const minAge = contextValues?.isOlderThanAgeOnDateMinAge;

      const startDate = dayjs(contextValues?.isOlderThanAgeOnDateStartDate);

      if (
        !dayProp ||
        !monthProp ||
        !startDate.isValid() ||
        minAge === undefined ||
        minAge === null ||
        !value
      ) {
        return false;
      }

      const objectValue = value as AnyObject;

      const monthValue = objectValue[monthProp];
      const dayValue = objectValue[dayProp];

      if (!objectValue.code || !monthValue?.code || !dayValue?.code) {
        return false;
      }

      const birthDate = dayjs(`${objectValue.code}-${monthValue.code}-${dayValue.code}`);

      if (!birthDate.isValid()) {
        return false;
      }

      const birthDateAdded = birthDate.add(minAge, 'year');

      return birthDateAdded.isBefore(startDate.add(1, 'day'), 'day');
    }
  });
});

yup.addMethod(
  yup.string,
  'isBirthDateYoungerThanMaxAgeOnDate',
  function isBirthDateYoungerThanMaxAgeOnDate(maxAge: number, message?: string) {
    return this.test({
      message,

      test(value, context) {
        const contextValues = context.options.context as StartDateContext | undefined;

        const startDate = dayjs(contextValues?.startDate);

        if (!startDate.isValid() || !value) {
          return false;
        }

        const birthDate = dayjs(value);

        return birthDate.add(maxAge, 'year').isSameOrAfter(startDate, 'day');
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'isBirthDateOlderThanMinAge',
  function isBirthDateOlderThanMinAge(minAge: number, message?: string) {
    return this.test({
      message,

      test(value, context) {
        const contextValues = context.options.context as StartDateContext | undefined;

        const startDate = dayjs(contextValues?.startDate);

        if (!startDate.isValid() || !value) {
          return false;
        }

        const birthDate = dayjs(value);

        return birthDate.add(minAge, 'year').isSameOrBefore(startDate, 'day');
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'isBirthDateYoungerThanMaxAgeOnCurrentDate',
  function isBirthDateYoungerThanMaxAgeOnCurrentDate(maxAge: number, message?: string) {
    return this.test({
      message,

      test(value) {
        if (!value) {
          return false;
        }

        const now = dayjs();
        const birthDate = dayjs(value);

        return birthDate.add(maxAge, 'year').isSameOrAfter(now, 'day');
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'isBirthDateOlderThanMinAgeOnCurrentDate',
  function isBirthDateOlderThanMinAgeOnCurrentDate(minAge: number, message?: string) {
    return this.test({
      message,

      test(value) {
        if (!value) {
          return false;
        }

        const now = dayjs();
        const birthDate = dayjs(value);

        return birthDate.add(minAge, 'year').isSameOrBefore(now, 'day');
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'isBirthDateOlderThanMinAgeOnStartDate',
  function isBirthDateOlderThanMinAgeOnStartDate(minAge: number, message?: string) {
    return this.test({
      message,

      test(value, context) {
        const contextValues = context.options.context as StartDateContext | undefined;

        const startDate = dayjs(contextValues?.startDate);

        if (!startDate.isValid() || !value) {
          return false;
        }

        const birthDate = dayjs(value);

        return birthDate.add(minAge, 'year').isSameOrBefore(startDate, 'day');
      }
    });
  }
);

yup.addMethod(yup.string, 'isAdultOnCurrentDate', function isAdultOnCurrentDate(message?: string) {
  return this.test({
    message,

    test(value, context) {
      const contextValues = context.options.context as AdultContext | undefined;

      const adultMinAge = contextValues?.adultMinAge;

      if (adultMinAge === undefined || adultMinAge === null || !value) {
        return false;
      }

      const now = dayjs();
      const birthDate = dayjs(value);
      return birthDate.add(adultMinAge, 'year').isSameOrBefore(now, 'day');
    }
  });
});

yup.addMethod(
  yup.string,
  'isOlderThanAgeOnDateString',
  function isOlderThanAgeOnDateString(message?: string) {
    return this.test({
      message,

      test(value, context) {
        const contextValues = context.options.context as AgeOnDateContext | undefined;

        const adultMinAge = contextValues?.adultMinAge;
        const startDate = dayjs(contextValues?.startDate);

        if (!startDate.isValid() || adultMinAge === undefined || adultMinAge === null || !value) {
          return false;
        }

        const birthDate = dayjs(value);

        const birthDateAdded = birthDate.add(adultMinAge, 'year');

        return birthDateAdded.isBefore(startDate.add(1, 'day'), 'day');
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'isOlderThanAgeOnDateStringExternal',
  function isOlderThanAgeOnDateStringExternal(message?: string) {
    return this.test({
      message,

      test(_value, context) {
        const contextValues = context.options.context as ExternalAgeContext | undefined;

        const adultMinAge = contextValues?.adultMinAge;

        const startDate = dayjs(contextValues?.startDate);

        const birthDate = dayjs(contextValues?.birthDate);

        if (
          !startDate.isValid() ||
          adultMinAge === undefined ||
          adultMinAge === null ||
          !birthDate.isValid()
        ) {
          return false;
        }

        const birthDateAdded = birthDate.add(adultMinAge, 'year');

        return birthDateAdded.isBefore(startDate.add(1, 'day'), 'day');
      }
    });
  }
);

yup.addMethod(yup.string, 'hasValidYear', function hasValidYear(message?: string) {
  return this.test({
    message,

    test(value, context) {
      const contextValues = context.options.context as ValidYearContext | undefined;

      const dateParts = value?.split('-');

      const year = dateParts?.find(item => item.length === 4) || '';

      const yearDigits = parseInt(year, 10);

      return !(
        isNaN(yearDigits) ||
        yearDigits < (contextValues?.minYearOfBirth || 1900) ||
        yearDigits > new Date().getFullYear()
      );
    }
  });
});

yup.addMethod(
  yup.string,
  'isOlderThanInfantOnDateString',
  function isOlderThanInfantOnDateString(message?: string) {
    return this.test({
      message,

      test(value, context) {
        const contextValues = context.options.context as ChildAgeContext | undefined;

        const childMinAge = contextValues?.childMinAge;
        const startDate = dayjs(contextValues?.startDate);

        if (!startDate.isValid() || childMinAge === undefined || childMinAge === null || !value) {
          return false;
        }

        const birthDate = dayjs(value);

        const birthDateAdded = birthDate.add(childMinAge, 'year');

        return birthDateAdded.isBefore(startDate.add(1, 'day'), 'day');
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'isYoungerThanAgeOnDateString',
  function isYoungerThanAgeOnDateString(message?: string) {
    return this.test({
      message,

      test(value, context) {
        const contextValues = context.options.context as AgeOnDateContext | undefined;

        const adultMinAge = contextValues?.adultMinAge;
        const startDate = dayjs(contextValues?.startDate);

        if (!startDate.isValid() || adultMinAge === undefined || adultMinAge === null || !value) {
          return false;
        }

        const birthDate = dayjs(value);

        const birthDateAdded = birthDate.add(adultMinAge, 'year');

        return birthDateAdded.isAfter(startDate, 'day');
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'isYoungerThanAgeOnDateStringExternal',
  function isYoungerThanAgeOnDateStringExternal(message?: string) {
    return this.test({
      message,

      test(_value, context) {
        const contextValues = context.options.context as ExternalAgeContext | undefined;

        const adultMinAge = contextValues?.adultMinAge;

        const startDate = dayjs(contextValues?.startDate);

        const birthDate = dayjs(contextValues?.birthDate);

        if (
          !startDate.isValid() ||
          adultMinAge === undefined ||
          adultMinAge === null ||
          !birthDate.isValid()
        ) {
          return false;
        }

        const birthDateAdded = birthDate.add(adultMinAge, 'year');

        return birthDateAdded.isAfter(startDate, 'day');
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'isYoungerOrEqalAgeThanAgeExternal',
  function isYoungerOrEqalAgeThanAgeExternal(message?: string) {
    return this.test({
      message,

      test(_value, context) {
        const contextValues = context.options.context as AgeComparisonContext | undefined;

        const startDate = dayjs(contextValues?.startDate);
        const birthDate = dayjs(contextValues?.birthDate);

        if (!startDate.isValid() || !birthDate.isValid()) {
          return false;
        }

        const diff = startDate.diff(birthDate, 'year');

        return diff <= (contextValues?.age ?? 0);
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'isExactlyAgeOnDateStringExternal',
  function isExactlyAgeOnDateStringExternal(message?: string) {
    return this.test({
      message,

      test(_value, context) {
        const contextValues = context.options.context as ExactAgeContext | undefined;

        const selectedAge = contextValues?.selectedAge;

        const selectedDate = dayjs(contextValues?.selectedDate);

        const birthDate = dayjs(contextValues?.birthDate);

        if (
          !selectedDate.isValid() ||
          selectedAge === undefined ||
          selectedAge === null ||
          !birthDate.isValid()
        ) {
          return false;
        }

        if (birthDate.isAfter(selectedDate, 'day')) {
          return false;
        }

        const actualAgeOnSelectedDate = selectedDate.diff(birthDate, 'year');

        return actualAgeOnSelectedDate === selectedAge;
      }
    });
  }
);

yup.addMethod(yup.string, 'isInfantOnDateString', function isInfantOnDateString(message?: string) {
  return this.test({
    message,

    test(value, context) {
      const contextValues = context.options.context as ChildAgeContext | undefined;

      const childMinAge = contextValues?.childMinAge;
      const startDate = dayjs(contextValues?.startDate);

      if (!startDate.isValid() || childMinAge === undefined || childMinAge === null || !value) {
        return false;
      }

      const birthDate = dayjs(value);
      const birthDateAdded = birthDate.add(childMinAge, 'year');
      return birthDateAdded.isAfter(startDate.add(1, 'day'), 'day');
    }
  });
});

yup.addMethod(yup.string, 'shouldNotBeNull', function shouldNotBeNull(message?: string) {
  return this.test({
    message,

    test(value) {
      return value !== null;
    }
  });
});

yup.addMethod(yup.string, 'equalEmails', function equalEmails() {
  return this.test({
    message: '${path} must match the configured email',

    test(value, context) {
      const contextValues = context.options.context as EmailContext | undefined;

      const emailProp = contextValues?.emailProp;

      if (!emailProp) {
        return true;
      }

      return value === this.parent[emailProp];
    }
  });
});

yup.addMethod(
  yup.string,
  'equalPasswords',
  function equalPasswords(passwordField: string, message?: string) {
    return this.test({
      name: 'equalPasswords',
      message,

      test(value) {
        return value === this.parent[passwordField];
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'notEqualField',
  function notEqualField(fieldName: string, message?: string) {
    return this.test({
      message,

      test(value) {
        if (!value) {
          return true;
        }

        const otherValue = this.parent?.[fieldName];

        if (!otherValue) {
          return true;
        }

        return value !== otherValue;
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'customRegExValidation',
  function customRegExValidation(regex: RegExp, message?: string) {
    return this.test({
      message,

      test(value) {
        if (!value) {
          return false;
        }

        return regex.test(value);
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'onlyNumbersNotAllowed',
  function onlyNumbersNotAllowed(message?: string) {
    return this.test({
      message,

      test(value) {
        const isOnlyNumbers = /^\d+$/.test((value || '').trim());

        return !isOnlyNumbers;
      }
    });
  }
);

yup.addMethod(
  yup.string,
  'specialCharactersNotAllowed',
  function specialCharactersNotAllowed(message?: string) {
    return this.test({
      message,

      test(value) {
        const specialCharacterRegex = /^[a-zA-Z0-9\s]*$/;

        return specialCharacterRegex.test(value || '');
      }
    });
  }
);

yup.addMethod(yup.string, 'onlySpacesNotAllowed', function onlySpacesNotAllowed(message?: string) {
  return this.test({
    message,

    test(value) {
      return (value || '').trim().length > 0;
    }
  });
});

yup.addMethod(yup.string, 'isNotAfterDate', function isNotAfterDate(message?: string) {
  return this.test({
    message,

    test(_value, context) {
      const contextValues = context.options.context as NotAfterDateContext | undefined;

      const vacationStartDate = dayjs(contextValues?.date);

      const birthDate = dayjs(contextValues?.birthDate);

      if (!vacationStartDate.isValid() || !birthDate.isValid()) {
        return false;
      }

      return !birthDate.isAfter(vacationStartDate, 'day');
    }
  });
});

const isDateRangeValid = (
  startDate: string | undefined,
  endDate: string | undefined,
  config: ConfigModel,
  dateRangeAllowSameDay?: boolean
): boolean => {
  if (!startDate || !endDate) {
    return false;
  }

  const dayjsStart = dayjs(startDate, config.formatDate);
  const dayjsEnd = dayjs(endDate, config.formatDate);

  if (!dayjsStart.isValid() || !dayjsEnd.isValid()) {
    return false;
  }
  if (dayjsStart.isAfter(dayjsEnd)) {
    return false;
  }
  if (!dateRangeAllowSameDay && dayjsStart.isSame(dayjsEnd)) {
    return false;
  }
  const now = dayjs().startOf('day');
  return dayjsStart.isSame(now) || dayjsStart.isAfter(now);
};

// VALIDATIONS BUILDER

const getBaseElementSchema = (schema: string, customSchemaMessage?: string): ElementSchema => {
  switch (schema) {
    case 'string':
      return customSchemaMessage ? yup.string().typeError(customSchemaMessage) : yup.string();

    case 'boolean':
      return customSchemaMessage ? yup.boolean().typeError(customSchemaMessage) : yup.boolean();

    case 'number':
      return customSchemaMessage ? yup.number().typeError(customSchemaMessage) : yup.number();

    case 'array':
      return customSchemaMessage ? yup.array().typeError(customSchemaMessage) : yup.array();

    case 'object':
      return customSchemaMessage ? yup.object().typeError(customSchemaMessage) : yup.object();

    default:
      return customSchemaMessage ? yup.string().typeError(customSchemaMessage) : yup.string();
  }
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const createElementSchema = (validations: Validations): ElementSchema => {
  let elementSchema = getBaseElementSchema(validations.schema, validations.customSchemaMessage);
  elementSchema = elementSchema.nullable();
  validations.types.forEach(type => {
    if ((elementSchema as any)[type.type]) {
      if (type.parameter) {
        elementSchema = (elementSchema as any)[type.type](type.parameter, type.customMessage || '');
      } else {
        elementSchema = (elementSchema as any)[type.type](type.customMessage || '');
      }
    }
  });
  return elementSchema;
};
/* eslint-enable */

const flattenStructure = (
  rows: FormRow[],
  keySuffix?: string | number
): Record<string, Validations> => {
  let allElements: Record<string, Validations> = {};
  rows.forEach(row => {
    row.elements.forEach(element => {
      if (element.validations) {
        allElements = {
          ...allElements,
          [`${element.key}${keySuffix || ''}`]: element.validations
        };
      }
    });
  });
  return allElements;
};

const createValidationSchema = (rows: FormRow[], keySuffix?: string | number): AnyObjectSchema => {
  const flattenedElements = flattenStructure(rows, keySuffix);

  return yup.object().shape(
    Object.entries<Validations | Record<string, Validations>>(flattenedElements).reduce(
      (schema, [key, item]) => {
        return {
          ...schema,
          [key]: isValidations(item)
            ? createElementSchema(item)
            : yup.object().shape(
                Object.entries<Validations>(item).reduce(
                  (innerSchema, [validationKey, validations]) => {
                    return {
                      ...innerSchema,
                      [validationKey]: createElementSchema(validations)
                    };
                  },
                  {}
                )
              )
        };
      },
      {}
    )
  );
};

const createInitialValues = <DataType extends FieldValues>(
  rows: FormRow[],
  keySuffix?: string | number,
  initialValues?: DataType & Record<string, unknown>
): DefaultValues<DataType> => {
  const values = rows.reduce<Record<string, unknown>>((total, currentRow) => {
    return {
      ...total,

      ...currentRow.elements.reduce<Record<string, unknown>>((totalInner, currentElement) => {
        const key = `${currentElement.key}${keySuffix || ''}`;

        return {
          ...totalInner,

          [key]:
            initialValues && initialValues[key] !== undefined && initialValues[key] !== null
              ? initialValues[key]
              : currentElement.initialValue
        };
      }, {})
    };
  }, {});

  if (initialValues) {
    Object.keys(initialValues).forEach(key => {
      if (!(key in values)) {
        values[key] = initialValues[key];
      }
    });
  }
  return values as DefaultValues<DataType>;
};

export { yup, isDateRangeValid, createValidationSchema, createInitialValues };
